import React, { useEffect, useState } from "react";
import { Radio } from "lucide-react";

/**
 * Subtle "live push" indicator for the C-Suite dashboard header.
 *
 * Props
 *  - lastUpdatedAt: number  Date.now() timestamp of the most recent successful
 *                            rollup refetch (whether initial load or a live push).
 *  - live: boolean          Whether the most recent refetch was triggered by a
 *                            websocket event (adds the green pulsing dot + "LIVE" label).
 *
 * Re-renders itself every 10s so the "Xs ago" text stays current without any
 * external timer.
 */
export default function LiveUpdatedPill({ lastUpdatedAt, live }) {
  const [, tick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 10_000);
    return () => clearInterval(t);
  }, []);

  if (!lastUpdatedAt) return null;

  const label = formatRelative(Date.now() - lastUpdatedAt);
  const isFresh = Date.now() - lastUpdatedAt < 60_000;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${
        live && isFresh
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-zinc-50 border-zinc-200 text-zinc-600"
      }`}
      title={live ? "Real-time push from the server" : "Last dashboard refresh"}
      data-testid="csuite-live-pill"
    >
      <span className="relative inline-flex h-2 w-2">
        {live && isFresh && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping"/>
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${live && isFresh ? "bg-emerald-500" : "bg-zinc-400"}`}/>
      </span>
      <Radio size={11} className="opacity-70"/>
      <span data-testid="csuite-live-pill-label">{live && isFresh ? "Live" : "Synced"} · {label}</span>
    </div>
  );
}

function formatRelative(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
